import React, { useContext, useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import TextField from '@material-ui/core/TextField'
import Typography from '@material-ui/core/Typography'
import Box from '@material-ui/core/Box'
import Alert from '@material-ui/lab/Alert'
import Button from '@material-ui/core/Button'

import FormDialog from '../../components/form-dialog/form-dialog.component'
import ConfirmDialog from '../../components/confirm-dialog/confirm-dialog.component'
import ActionButtons from '../../components/action-buttons/action-buttons.component'
import DialogAlert from '../../components/alert/alert.component'
import TableContainer from '../../components/table-container/table-container'
import ActiveToggle from '../../components/active-toggle/active-toggle.component'
import MultipleSelect from '../../components/multi-select/multi-select'
import ManyToManyInput from '../../components/many-many-input/many-many-input.component'

import AppInsightsTrackingContext from '../../context/app-insights-tracking/AppInsightsTrackingContext'
import RequestService from '../../services/request/request-service'

import {
  ACTIONS_COLUMN,
  BASE_TABLE_COLUMNS,
  DEFAULT_ROW_DATA,
  DEFAULT_TABLE_CONFIGURATION,
} from '../../shared/constants/table'
import { DEFAULT_DEPENDENCIES_ERROR_MESSAGE, DEFAULT_COMFIRM_INACTIVE_MESSAGE } from '../../shared/constants/messages'
import { DELETE_METHOD, POST_METHOD, PUT_METHOD } from '../../shared/constants/requests'
import { ALERT_TYPES, INCLUDE_INACTIVE_FLAG } from '../../shared/constants/general'
import { STEM_CONDITION_QUERY, VARIETIES_STEM_CONDITIONS_QUERY, VARIETY_QUERY } from '../../shared/constants/queries'
import { buildPayload, getManyToManyData } from '../../shared/utils/utils'
import { LOG_PAGE_LOADS } from '../../shared/constants/logging'
import useHttpInactive from '../../shared/hooks/useHttpInactive'

const StemCondition = ({ userEmail }) => {
  const history = useHistory()
  const { t } = useTranslation()
  const { trackEvent } = useContext(AppInsightsTrackingContext)

  const [openDialog, setOpen] = useState(false)
  const [openDelDialog, setDelOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [modalType, setModalType] = useState('Add new')
  const [rowData, setRowData] = useState(DEFAULT_ROW_DATA)
  const [rows, setRows] = useState([])
  const [tableConf, setTableConf] = useState(DEFAULT_TABLE_CONFIGURATION)
  const [openAlert, setOpenAlert] = useState(false)
  const [severity, setSeverity] = useState(ALERT_TYPES.success)
  const [deletable, setDeletable] = useState(true)
  const [showActive, setShowActive] = useState(true)
  const [openVarieties, setOpenVarieties] = useState(false)
  const [selectedVarieties, setSelectedVarieties] = useState([])
  const [varieties, setVarieties] = useState([])
  const { executeInactive, loadingInactive } = useHttpInactive(STEM_CONDITION_QUERY)
  // columns definition for table
  const translatedColumns = BASE_TABLE_COLUMNS.map((column) => ({
    ...column,
    headerName: t(column.headerName),
  }))

  const columns = [
    ...translatedColumns,
    {
      field: 'varieties',
      headerName: t('varieties'),
      flex: 1,
      minWidth: 135,
      renderCell: (params) => (
        <Button
          color="secondary"
          onClick={() => {
            setRowData({ id: params.row.id })
            setOpenVarieties(true)
          }}
          style={{ textTransform: 'none' }}
        >
          {t('see_varieties')}
        </Button>
      ),
    },
    {
      ...ACTIONS_COLUMN,
      headerName: t(ACTIONS_COLUMN.headerName),
      renderCell: (params) => ActionButtons(params.row, handleClickOpen, handleClickDelOpen),
    },
  ]

  useEffect(() => {
    trackEvent(LOG_PAGE_LOADS.LOAD_STEM_CONDITION_PAGE, { email: userEmail })
  }, [])

  // load data
  useEffect(() => {
    loadData()
  }, [tableConf, showActive])

  const loadData = async () => {
    try {
      setRows([])
      setLoading(true)
      let url = `${STEM_CONDITION_QUERY}?${!showActive ? INCLUDE_INACTIVE_FLAG : ''}&startRowIndex=${
        tableConf.page - 1
      }&pageSize=10000`
      let response = await RequestService.Get(url, history)
      setRows(response.data)

      url = `${VARIETY_QUERY}?startRowIndex=0&pageSize=10000`
      response = await RequestService.Get(url, history)
      setVarieties(response.data)
    } catch (error) {
      console.error(error)
      setSeverity(ALERT_TYPES.error)
      setOpenAlert(true)
    } finally {
      setLoading(false)
    }
  }

  // insert and edit
  const insertEditData = async (method, data) => {
    setLoading(true)
    setOpen(false)

    let id = ''
    if (method === DELETE_METHOD) {
      id = `?id=${data.id}`
    }
    let url = `${STEM_CONDITION_QUERY}${id}`
    let response
    if (method === POST_METHOD) response = await RequestService.Post(url, history, data)
    if (method === PUT_METHOD) response = await RequestService.Put(url, history, data)
    if (response?.hasError) {
      console.error(response.errorMessage)
      setSeverity(ALERT_TYPES.error)
      return
    }
    // Created stem condition id
    id = response.data.id

    const stemConditionVarietiesPayload = buildPayload(id, selectedVarieties, null, true)
    url = `${VARIETIES_STEM_CONDITIONS_QUERY}?filterByFirst=false`
    response = await RequestService.Put(url, history, stemConditionVarietiesPayload, { isBlankInstance: true })
    if (response?.hasError) {
      console.error(response.errorMessage)
      setSeverity(ALERT_TYPES.error)
      return
    }

    setTableConf({ ...tableConf })
    setSeverity(ALERT_TYPES.success)
    setOpenAlert(true)
    setLoading(false)
    setSelectedVarieties([])
  }

  // Form Dialog handler
  const handleClickOpen = (type, params) => async () => {
    if (type === 'Edit') {
      trackEvent('EDIT_CLICK', { email: userEmail })
      const sizesVarieties = await getManyToManyData(
        `${VARIETIES_STEM_CONDITIONS_QUERY}?id=${params.id}&filterByFirst=false`,
        history,
        varieties
      )

      setSelectedVarieties(sizesVarieties)

      setRowData({
        id: params.id,
        name: params.name,
        description: params.description,
        active: params.active,
      })
    } else {
      trackEvent('ADD_CLICK', { email: userEmail })
      setRowData({ ...DEFAULT_ROW_DATA })
      setSelectedVarieties([])
    }
    setModalType(type)
    setOpen(true)
  }

  const handleSubmit = (event) => {
    trackEvent('SUBMIT_CLICK', { email: userEmail })
    event.preventDefault()
    const data = { ...rowData }
    const method = modalType === 'Edit' ? PUT_METHOD : POST_METHOD
    insertEditData(method, data)
  }

  // Confirm Dialog handlers
  const handleClickDelOpen = (params) => () => {
    trackEvent('DELETE_CLICK', { email: userEmail })
    setRowData({
      id: params.id,
      name: params.name,
      description: params.description,
      active: !params.active,
    })
    setDelOpen(true)
  }

  const onClickDelConfirmation = async () => {
    const { success } = await executeInactive(rowData.id)
    if (success) {
      setDelOpen(false)
      loadData()
    }
  }

  // Form on change
  const handleChange = (event) => {
    const { name, value } = event.target
    setRowData({
      ...rowData,
      [name]: value,
    })
  }

  const handleErrorClose = (event, reason) => {
    if (reason === 'clickaway') return
    setOpenAlert(false)
  }

  return (
    <div>
      <Box pt={5} pb={3}>
        <Typography component="h1" variant="h5" color="primary">
          {t('stem_condition')}
        </Typography>
      </Box>
      <TableContainer
        haveSearchInput="true"
        handleClickOpen={() => handleClickOpen('Add new', null)}
        columns={columns}
        rows={rows}
        tableConfiguration={tableConf}
        setTableConf={setTableConf}
        onToggleClick={() => setShowActive(!showActive)}
        showActive={showActive}
        loading={loading}
      />
      <MultipleSelect
        open={openVarieties}
        onClose={() => {
          setOpenVarieties(false)
        }}
        title={t('varieties')}
        getUrl="variety"
        getRelationUrl="varietiesstemconditions"
        useLast
        mainId={rowData.id}
        lefttoright
      />
      <FormDialog
        title={`${modalType} stem condition`}
        open={openDialog}
        onClose={() => {
          setOpen(false)
        }}
        handleSubmit={handleSubmit}
        loading={loading}
      >
        <TextField
          defaultValue={rowData.name}
          autoFocus
          variant="outlined"
          margin="normal"
          name="name"
          label={t('name')}
          type="text"
          fullWidth
          onChange={handleChange}
          required
        />
        <TextField
          defaultValue={rowData.description}
          variant="outlined"
          margin="normal"
          name="description"
          label={t('description')}
          type="text"
          fullWidth
          onChange={handleChange}
        />
        <ManyToManyInput
          inputLabel={t('varieties')}
          name="id_variety"
          selectedValues={selectedVarieties}
          allValues={varieties}
          setSelectedValues={setSelectedVarieties}
        />
        {modalType === 'Edit' && (
          <ActiveToggle value={rowData.active} onToggle={() => setRowData({ ...rowData, active: !rowData.active })} />
        )}
      </FormDialog>
      <ConfirmDialog
        title={t('deactivate_stem_condition')}
        okButton="Deactivate"
        open={openDelDialog}
        loading={loadingInactive}
        onClose={() => {
          setDelOpen(false)
          setDeletable(true)
        }}
        onOk={onClickDelConfirmation}
      >
        {t(DEFAULT_COMFIRM_INACTIVE_MESSAGE)} {rowData.name}?
        {!deletable && (
          <Alert style={{ marginTop: 10 }} severity="error">
            {t(DEFAULT_DEPENDENCIES_ERROR_MESSAGE)}
          </Alert>
        )}
      </ConfirmDialog>
    </div>
  )
}

const mapStateToProps = (state) => ({
  userEmail: state.user.email,
})

export default connect(mapStateToProps, null)(StemCondition)
